package nl.volleyplanner.util

import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/**
 * MutableMap adapter that can safely be used in coroutines on all Kotlin platforms
 */
class CoroutineSafeMap<K, V> {
    private val mutableMap = mutableMapOf<K, V>()
    private val mutex = Mutex()

    suspend fun getEntries() = mutex.withLock { mutableMap.toMap().entries }
    suspend fun getKeys() = mutex.withLock { mutableMap.keys.toSet() }
    suspend fun getSize() = mutex.withLock { mutableMap.size }
    suspend fun getValues() = mutex.withLock { mutableMap.values.toList() }

    suspend fun clear() {
        mutex.withLock { mutableMap.clear() }
    }

    suspend fun isEmpty(): Boolean {
        return mutex.withLock { mutableMap.isEmpty() }
    }

    suspend fun remove(key: K): V? {
        return mutex.withLock { mutableMap.remove(key) }
    }

    suspend fun putAll(from: Map<out K, V>) {
        return mutex.withLock { mutableMap.putAll(from) }
    }

    suspend fun put(key: K, value: V): V? {
        return mutex.withLock { mutableMap.put(key, value) }
    }

    suspend fun putIfAbsent(key: K, value: V): Boolean {
        return mutex.withLock {
            if (mutableMap.containsKey(key)) {
                false
            } else {
                mutableMap[key] = value
                true
            }
        }
    }

    suspend fun getOrPut(key: K, defaultValue: () -> V): V {
        return mutex.withLock {
            mutableMap.getOrPut(key, defaultValue)
        }
    }

    suspend fun get(key: K): V? {
        return mutex.withLock { mutableMap.get(key) }
    }

    suspend fun containsValue(value: V): Boolean {
        return mutex.withLock { mutableMap.containsValue(value) }
    }

    suspend fun containsKey(key: K): Boolean {
        return mutex.withLock { mutableMap.containsKey(key) }
    }
}